import { ProgressLoader, Search, Text, VSpacer } from '@/components/DesignSystem';
import ERPRetailerSelector from "@/components/shared/ListSelector/ERPRetailerSelector";
import { QueryKeys } from '@/constants/QueryKeys';
import { useAuthentication } from "@/contexts/dataSync/AuthenticationContext";
import { useSearch } from '@/hooks/useSearch';
import { ManufacturerApi } from '@/utilities/api/ManufacturerApi';
import { ManufacturerEndpoint } from "@api/endpoints";
import { ApiManufacturer, ApiRetailer } from '@api/interfaces';
import { Container, Divider, Stack } from '@mui/material';
import React, { Fragment, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from "react-router-dom";
import ManufacturerListItem from './ManufacturerListItem';

enum QueryParams {
  'search' = 'search',
}

const ManufacturerList = () => {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const initialSearch = searchParams.get(QueryParams.search) ?? '';
  const { search, setSearch, debouncedSearch } = useSearch(3, initialSearch);

  const { retailer } = useAuthentication();
  const [selectedRetailer, setSelectedRetailer] = useState<ApiRetailer | undefined>(retailer);

  const query : ManufacturerEndpoint.List.Query = {
    retailerId: selectedRetailer?.id,
    search: debouncedSearch,
  };

  const { data: manufacturersList, isFetching: manufacturersLoading } = useQuery(
    [QueryKeys.GET_MANUFACTURERS, debouncedSearch, selectedRetailer?.id],
    () => ManufacturerApi.list(query),
    {
      enabled: !!selectedRetailer?.id,
      staleTime: 5 * 60 * 1000,
    },
  );

  useEffect(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set(QueryParams.search, debouncedSearch);
      return newParams;
    });
  }, [debouncedSearch]);

  const getManufacturerCountText = () => {
    if (selectedRetailer) {
      return manufacturersList?.length === 1
        ? `1 ${selectedRetailer.name} manufacturer`
        : `${manufacturersList?.length} ${selectedRetailer.name} manufacturers`;
    }
    return manufacturersList?.length === 1
      ? '1 manufacturer (generic)'
      : `${manufacturersList?.length} manufacturers (generic)`;
  };

  const onRetailerSelected = (retailer?: ApiRetailer) => {
    if (retailer === undefined || retailer?.id !== selectedRetailer?.id) {
      setSelectedRetailer(retailer);
    }
  };

  const LoadingSpinner = (
    <Stack alignItems="center">
      <ProgressLoader type="circular" />
    </Stack>
  );

  return (
    <Container maxWidth='lg'>
      <Stack>
        <ERPRetailerSelector
          header='Manufacturers'
          logo="manufacturer"
          onSelect={onRetailerSelected}
        />
        <VSpacer size='6' />
        <Divider />
        <VSpacer size='6' />
        <Stack
          alignItems='center'
          direction='row'
          justifyContent='space-between'
        >
          <Search
            onChangeText={setSearch}
            testID='counties-modal-search'
            value={search}
            width={289}
          />
        </Stack>
        {!!manufacturersList && manufacturersList.length > 0 && (
          <>
            <VSpacer size='6' />
            <Text>{getManufacturerCountText()}</Text>
          </>
        )}
        <VSpacer size='5' />
        {manufacturersLoading && LoadingSpinner}
        {!manufacturersLoading && manufacturersList?.length === 0 && <Text>No results found</Text>}
        {!manufacturersLoading && manufacturersList?.map((manufacturer: ApiManufacturer) => (
          <Fragment key={manufacturer.id}>
            <ManufacturerListItem
              key={manufacturer.id}
              manufacturer={manufacturer}
              onClick={() => navigate(`/admin/manufacturers/${manufacturer.id}`)}
              showActions={false}
            />
          </Fragment>
        ))}
      </Stack>
    </Container>
  );
};

export default ManufacturerList;
